import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Box, Skeleton, Text, useTheme } from '@ublend-npm/aulaui-core';
import { getHomeUserFirstName } from '@app/selectors/home';
import GreetingTimeIcon, {
  getGreetingTimeOfDay,
  GREETING_TIME_LABELS,
  GreetingTimeOfDay,
} from './GreetingTimeIcon';
import { HOME_WIDGET_FONT_FAMILY } from '../homeWidgetTypography';

/** Figma greeting header — icon + text on page background (not a card). */
const GREETING_ICON_SIZE = '2.5625rem'; // 41px
const GREETING_ICON_TEXT_GAP = '0.8125rem'; // 13px
const GREETING_ROW_HEIGHT = '2.8125rem'; // 45px

const greetingFocusSx = {
  outline: 'none',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px',
  },
};

const HomeGreetingSkeleton = () => (
  <Box
    component="section"
    id="home-greeting-widget"
    tabIndex={0}
    role="status"
    aria-live="polite"
    aria-busy="true"
    aria-label="Loading greeting"
    sx={{
      ...greetingFocusSx,
      display: 'flex',
      alignItems: 'center',
      gap: GREETING_ICON_TEXT_GAP,
      minHeight: GREETING_ROW_HEIGHT,
      width: '100%',
      minWidth: 0,
    }}
  >
    <Skeleton kind="circular" width={41} height={41} sx={{ flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton
        kind="rounded"
        height={20}
        sx={{ width: '12rem', maxWidth: '100%', borderRadius: '0.25rem' }}
      />
      <Skeleton
        kind="rounded"
        height={16}
        sx={{
          width: '9.5rem',
          maxWidth: '100%',
          borderRadius: '0.25rem',
          mt: '0.25rem',
        }}
      />
    </Box>
  </Box>
);

const HomeGreeting = () => {
  const theme = useTheme();
  const { palette } = theme;
  const firstName = useSelector(getHomeUserFirstName);
  const [timeOfDay, setTimeOfDay] = useState<GreetingTimeOfDay | null>(null);

  useEffect(() => {
    setTimeOfDay(getGreetingTimeOfDay());
  }, []);

  if (!timeOfDay) {
    return <HomeGreetingSkeleton />;
  }

  const greetingLabel = GREETING_TIME_LABELS[timeOfDay];
  const headingText = firstName
    ? `${greetingLabel}, ${firstName}`
    : greetingLabel;
  const todayLabel = moment().format('[Today is] Do MMMM YYYY');

  return (
    <Box
      component="section"
      id="home-greeting-widget"
      tabIndex={0}
      aria-label="Greeting Widgets"
      aria-labelledby="home-greeting-heading"
      sx={{
        ...greetingFocusSx,
        display: 'flex',
        alignItems: 'center',
        gap: GREETING_ICON_TEXT_GAP,
        minHeight: GREETING_ROW_HEIGHT,
        width: '100%',
        minWidth: 0,
      }}
    >
      <GreetingTimeIcon
        timeOfDay={timeOfDay}
        sx={{
          width: GREETING_ICON_SIZE,
          height: GREETING_ICON_SIZE,
          flexShrink: 0,
          '& svg': { width: '100%', height: '100%', display: 'block' },
        }}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Text
          component="h2"
          id="home-greeting-heading"
          tabIndex={0}
          sx={{
            m: 0,
            p: 0,
            fontFamily: HOME_WIDGET_FONT_FAMILY,
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.15px',
            color: palette.primary.main,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {headingText}
        </Text>

        <Text
          component="time"
          tabIndex={0}
          sx={{
            m: 0,
            p: 0,
            display: 'block',
            fontFamily: HOME_WIDGET_FONT_FAMILY,
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            letterSpacing: '0.17px',
            color: palette.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {todayLabel}
        </Text>
      </Box>
    </Box>
  );
};

export default HomeGreeting;
